// UX-048: naming the last crumb of a drilled-in screen. Plain functions, kept out of
// Breadcrumbs.tsx so that file exports only components.

const OPAQUE_ID = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|[A-Za-z0-9_-]*\d[A-Za-z0-9_-]*)$/i

/** What the drilled-in screen is called when its own name is not known (yet). */
const ENTITY_WORD: Record<string, string> = {
  project: 'Project',
  document: 'Document',
  folder: 'Folder',
  ecosystem: 'Profile',
  journals: 'Journal',
  patents: 'Patent',
}

export interface EntityNames {
  project?: (id: string) => string | undefined
  document?: (id: string) => string | undefined
}

/**
 * UX-048: the last crumb of a drilled-in screen is the thing's own name — the
 * project's, the document's, the entity group's — never the word "Detail".
 * While a name has not loaded (or the thing has none), it falls back to what kind
 * of thing it is ("Project"), and never to a raw id.
 */
export function entityCrumbLabel(pathname: string, names: EntityNames = {}): string | undefined {
  const parts = pathname.split('/').filter(Boolean)
  const decode = (v: string) => {
    try {
      return decodeURIComponent(v)
    } catch {
      return v
    }
  }
  // /agg/entity/group/:groupName — the group's own name is in the path.
  const group = parts.indexOf('group')
  if (parts[0] === 'agg' && parts[1] === 'entity' && group > 0 && parts[group + 1]) {
    return decode(parts[group + 1])
  }
  // The deepest "<kind>/<id>" pair wins: /project/:id/folder/:fid is a folder screen.
  for (let i = parts.length - 2; i >= 0; i--) {
    const kind = parts[i]
    const id = parts[i + 1]
    if (!ENTITY_WORD[kind] || !id) continue
    if (kind === 'project') return names.project?.(id)?.trim() || ENTITY_WORD.project
    if (kind === 'document') return names.document?.(id)?.trim() || ENTITY_WORD.document
    return ENTITY_WORD[kind]
  }
  // Anything else: a readable last segment, or nothing — never an opaque id.
  const tail = parts[parts.length - 1]
  if (!tail || OPAQUE_ID.test(tail)) return undefined
  const words = decode(tail).replace(/[-_]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : undefined
}
